(() => {
  'use strict';

  // The app's current select options remain authoritative for offer restrictions
  // and inventory. Match their IDs to structured Shopify options, never split titles.
  function choicesFor(config, options) {
    const variants = new Map(config.variants.map((variant) => [String(variant.id), variant]));
    return options.flatMap((option) => {
      const variant = variants.get(String(option.id));
      if (!variant || variant.options.length !== 2) return [];
      const model = variant.options[config.modelIndex];
      const color = variant.options[config.colorIndex];
      if (!model || !color) return [];
      return [{
        id: String(variant.id), model, color,
        available: variant.available !== false && !option.disabled && option.available !== 'false'
      }];
    });
  }

  function variantForModel(choices, model, color) {
    const available = choices.filter((choice) => choice.model === model && choice.available);
    return available.find((choice) => choice.color === color) || available[0] || null;
  }

  // Node's built-in runner can exercise the same resolver used by the browser.
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { choicesFor, variantForModel };
  }
  if (typeof customElements === 'undefined' || customElements.get('xtrem-bundle-picker')) return;

  const widgetSelector = '#recommended-offer-widget[data-offer-kind="qb"]';
  const selectSelector = '[data-bundle-variant-mode="combo"] [data-bundle-variant-row] select[data-bundle-value-mode="variant-id"]';
  let sequence = 0;

  class XtremBundlePicker extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      try {
        this.config = JSON.parse(this.querySelector('script[type="application/json"]').textContent);
      } catch (_) { return; }
      this.scope = this.closest('product-info');
      if (!this.scope || !Array.isArray(this.config.variants) || this.config.variants.length === 0
        || !Array.isArray(this.config.colors) || !this.config.labels
        || ![0, 1].includes(this.config.modelIndex) || this.config.colorIndex !== 1 - this.config.modelIndex) return;
      this.controller = new AbortController();
      this.entries = new Map();
      const schedule = () => this.schedule();
      // Capture non-bubbling app updates too. Reconcile after its synchronous handlers.
      ['change', 'click', 'bundle-widget:options-updated', 'recommended-offer:loaded'].forEach((name) => {
        this.scope.addEventListener(name, schedule, { capture: true, signal: this.controller.signal });
      });
      this.observer = new MutationObserver((records) => {
        if (records.some((record) => !record.target.closest?.('.xtrem-bundle-choice'))) this.schedule();
      });
      this.observer.observe(this.scope, {
        childList: true, subtree: true, characterData: true, attributes: true,
        attributeFilter: ['disabled', 'selected', 'data-bundle-available', 'data-bundle-value-mode', 'data-bundle-variant-mode']
      });
      this.sync();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.entries?.forEach((entry) => this.releaseEntry(entry));
      this.entries?.clear();
      this.controller = null;
    }

    schedule() {
      if (this.pending || !this.controller) return;
      this.pending = true;
      queueMicrotask(() => {
        this.pending = false;
        if (this.isConnected && this.controller) this.sync();
      });
    }

    sync() {
      const widget = this.scope.querySelector(widgetSelector);
      const productId = widget?.getAttribute('data-product-gid')?.split('/').pop();
      const selects = productId === String(this.config.productId)
        ? Array.from(widget.querySelectorAll(selectSelector)) : [];
      this.entries.forEach((entry, select) => {
        if (!selects.includes(select) || !entry.details.isConnected) {
          this.releaseEntry(entry);
          this.entries.delete(select);
        }
      });
      selects.forEach((select) => {
        const options = Array.from(select.options).map((option) => ({
          id: option.value, disabled: option.disabled, available: option.dataset.bundleAvailable
        }));
        const choices = choicesFor(this.config, options);
        const selected = choices.find((choice) => choice.id === select.value);
        // Fail open if the app changes its schema, product, or adds unmapped variants.
        if (!selected || choices.length !== options.length || select.closest('[data-bundle-variant-row]').querySelectorAll('select').length !== 1) {
          const entry = this.entries.get(select);
          if (entry) { this.releaseEntry(entry); this.entries.delete(select); }
          return;
        }
        let entry = this.entries.get(select);
        if (!entry) {
          entry = this.create(select);
          this.entries.set(select, entry);
        }
        entry.choices = choices;
        entry.selected = selected;
        const signature = JSON.stringify([choices, selected.id, select.disabled]);
        if (entry.signature !== signature) {
          entry.signature = signature;
          this.render(entry);
        }
      });
    }

    create(select) {
      const row = select.closest('[data-bundle-variant-row]');
      const badge = row.querySelector('.recommended-offer-widget__variant-badge')?.textContent.trim() || '';
      const details = document.createElement('details');
      details.className = 'xtrem-bundle-choice';
      const summary = document.createElement('summary');
      summary.className = 'xtrem-bundle-choice__summary';
      const text = document.createElement('span');
      text.className = 'xtrem-bundle-choice__result';
      text.setAttribute('aria-live', 'polite');
      text.setAttribute('aria-atomic', 'true');
      summary.append(text);
      const panel = document.createElement('div');
      panel.className = 'xtrem-bundle-choice__panel';
      const modelGroup = document.createElement('fieldset');
      const colorGroup = document.createElement('fieldset');
      const done = document.createElement('button');
      done.type = 'button';
      done.className = 'xtrem-bundle-choice__done';
      done.textContent = this.config.labels.done;
      panel.append(modelGroup, colorGroup, done);
      details.append(summary, panel);
      const entry = { select, row, badge, details, summary, text, modelGroup, colorGroup, done,
        id: `xtrem-bundle-${++sequence}`, controller: new AbortController() };
      const options = { signal: entry.controller.signal };
      // The tier treats bubbled clicks/Space/Enter as a new tier selection.
      details.addEventListener('click', (event) => event.stopPropagation(), options);
      details.addEventListener('keydown', (event) => {
        event.stopPropagation();
        if (event.key === 'Escape' && details.open) {
          event.preventDefault();
          details.open = false;
          summary.focus();
        }
      }, options);
      details.addEventListener('toggle', () => {
        if (!details.open) return;
        this.sync();
        this.entries.forEach((other) => { if (other !== entry) other.details.open = false; });
      }, options);
      details.addEventListener('change', (event) => {
        const input = event.target;
        if (!input.matches('input[data-choice]')) return;
        event.stopPropagation();
        // Inventory and offer options may have changed since the panel was opened.
        this.sync();
        if (!this.entries.has(select) || select.disabled) return;
        const choice = input.dataset.choice === 'model'
          ? variantForModel(entry.choices, input.value, entry.selected.color)
          : entry.choices.find((item) => item.model === entry.selected.model && item.color === input.value && item.available);
        if (!choice) { this.render(entry); return; }
        if (select.value !== choice.id) {
          select.value = choice.id;
          // Bun Bundles reads this select for each case, updates its price and builds cart lines.
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
        this.sync();
      }, options);
      done.addEventListener('click', () => { details.open = false; summary.focus(); }, options);
      select.after(details);
      row.classList.add('xtrem-bundle-row');
      select.classList.add('xtrem-bundle-native');
      return entry;
    }

    render(entry) {
      const { selected, choices, select } = entry;
      entry.text.textContent = `${selected.color} / ${selected.model}`;
      entry.summary.setAttribute('aria-label', `${entry.badge} ${entry.text.textContent}`.trim());
      const models = [...new Set(choices.map((choice) => choice.model))].map((model) => ({
        value: model, available: choices.some((choice) => choice.model === model && choice.available)
      }));
      const colors = choices.filter((choice) => choice.model === selected.model).map((choice) => ({
        value: choice.color, available: choice.available,
        swatch: this.config.colors.find((color) => color.name === choice.color)?.swatch
      }));
      this.renderGroup(entry, entry.modelGroup, 'model', models, selected.model);
      this.renderGroup(entry, entry.colorGroup, 'color', colors, selected.color);
      entry.modelGroup.disabled = entry.colorGroup.disabled = select.disabled;
    }

    renderGroup(entry, fieldset, kind, values, selected) {
      const signature = JSON.stringify(values);
      if (fieldset.dataset.choices !== signature) {
        const focusValue = fieldset.contains(document.activeElement) ? document.activeElement.value : null;
        const legend = document.createElement('legend');
        legend.textContent = this.config.labels[kind];
        const grid = document.createElement('div');
        grid.className = `xtrem-bundle-choice__options xtrem-bundle-choice__options--${kind}`;
        values.forEach((value, index) => {
          const label = document.createElement('label');
          label.className = 'xtrem-bundle-choice__option';
          const input = document.createElement('input');
          input.type = 'radio';
          input.name = `${entry.id}-${kind}`;
          input.id = `${entry.id}-${kind}-${index}`;
          input.dataset.choice = kind;
          input.value = value.value;
          input.disabled = !value.available;
          const face = document.createElement('span');
          face.className = 'xtrem-bundle-choice__face';
          if (value.swatch && CSS.supports('color', value.swatch)) {
            const swatch = document.createElement('span');
            swatch.className = 'xtrem-bundle-choice__swatch';
            swatch.style.backgroundColor = value.swatch;
            swatch.setAttribute('aria-hidden', 'true');
            face.append(swatch);
          }
          const name = document.createElement('span');
          name.textContent = value.value;
          face.append(name);
          if (!value.available) {
            const status = document.createElement('span');
            status.className = 'xtrem-bundle-choice__unavailable';
            status.textContent = this.config.labels.soldOut;
            face.append(status);
          }
          label.append(input, face);
          grid.append(label);
        });
        fieldset.replaceChildren(legend, grid);
        fieldset.dataset.choices = signature;
        if (focusValue) Array.from(fieldset.querySelectorAll('input')).find((input) => input.value === focusValue && !input.disabled)?.focus();
      }
      fieldset.querySelectorAll('input').forEach((input) => { input.checked = input.value === selected; });
    }

    releaseEntry(entry) {
      entry.controller.abort();
      entry.details.remove();
      entry.select.classList.remove('xtrem-bundle-native');
      entry.row.classList.remove('xtrem-bundle-row');
    }
  }
  customElements.define('xtrem-bundle-picker', XtremBundlePicker);
})();
